import { useEffect, useRef, useState } from "react"
import { useRouter } from "next/router"

import { useClerk, useOrganization, useOrganizationList } from "@clerk/nextjs"
import type { OrganizationResource, SessionResource } from "@clerk/nextjs/types"
import {
  Avatar,
  Button,
  MenuTrigger,
  Popover,
  PopoverContent,
  PopoverItem,
  PopoverTrigger,
  SearchInput,
  toast,
} from "@coval/ui"

import { useSyncActiveOrganization } from "@/hooks/useSyncActiveOrganization"

export function OrganizationPicker({
  onNavigate,
}: {
  onNavigate?: () => void
}) {
  const clerk = useClerk()
  const router = useRouter()
  const lifecycle = useRef({ mounted: true })
  useEffect(() => {
    const state = lifecycle.current
    state.mounted = true
    return () => {
      state.mounted = false
    }
  }, [])
  const { organization } = useOrganization()
  const { isLoaded, setActive, userMemberships } = useOrganizationList({
    userMemberships: { pageSize: 50, keepPreviousData: true },
  })
  const { navigateAfterOrgSwitch } = useSyncActiveOrganization()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")
  const [switching, setSwitching] = useState(false)
  const lock = useRef(false)
  const switchOrganization = async (next: OrganizationResource) => {
    if (!setActive || lock.current || next.id === organization?.id) {
      return
    }
    lock.current = true
    setSwitching(true)
    const userId = clerk.user?.id
    const sessionId = clerk.session?.id
    const previousPath = router.asPath
    const previousOrganizationId = organization?.id
    let navigationSession: SessionResource | undefined
    try {
      const resolved = await clerk.getOrganization(next.id)
      if (
        !lifecycle.current.mounted ||
        clerk.user?.id !== userId ||
        router.asPath !== previousPath
      ) {
        return
      }
      if (!resolved.slug) {
        throw new Error(
          "This organization has no navigation address. Contact support.",
        )
      }
      await setActive({
        organization: resolved.id,
        navigate: async ({ session, decorateUrl }) => {
          // Clerk clears its accessors during navigation; use the supplied session.
          if (
            session.user?.id !== userId ||
            session.id !== sessionId ||
            session.lastActiveOrganizationId !== resolved.id ||
            router.asPath !== previousPath
          ) {
            return
          }
          navigationSession = session
          await navigateAfterOrgSwitch(resolved, decorateUrl)
        },
      })
      if (lifecycle.current.mounted) {
        setOpen(false)
        onNavigate?.()
      }
    } catch (error) {
      // A rejected navigate callback leaves Clerk's accessors in transitive state.
      if (
        navigationSession &&
        previousOrganizationId &&
        navigationSession.user?.id === userId &&
        navigationSession.id === sessionId &&
        navigationSession.lastActiveOrganizationId === next.id &&
        (clerk.session === undefined || clerk.session?.id === sessionId) &&
        clerk.client?.lastActiveSessionId === sessionId &&
        router.asPath === previousPath
      ) {
        try {
          await setActive({
            session: sessionId,
            organization: previousOrganizationId,
          })
        } catch {
          toast.error({
            error,
            title:
              "Could not restore your organization. Reload this page to continue.",
          })
          return
        }
      }
      if (
        (!lifecycle.current.mounted && !navigationSession) ||
        clerk.user?.id !== userId ||
        router.asPath !== previousPath
      ) {
        return
      }
      toast.error({
        error,
        title:
          error instanceof Error
            ? error.message
            : "Could not switch organizations. Please retry.",
      })
    } finally {
      lock.current = false
      if (lifecycle.current.mounted) {
        setSwitching(false)
      }
    }
  }
  if (!organization) {
    return null
  }
  const memberships = [...(userMemberships?.data ?? [])].sort((a, b) =>
    a.organization.name.localeCompare(b.organization.name, undefined, {
      sensitivity: "base",
    }),
  )
  const filtered = memberships.filter(
    (membership) =>
      membership.organization.id !== organization.id &&
      [membership.organization.name, membership.organization.slug].some(
        (value) => value?.toLowerCase().includes(search.trim().toLowerCase()),
      ),
  )
  const visibleOrganizations = [
    organization,
    ...filtered.map((membership) => membership.organization),
  ]
  return (
    <Popover
      modal
      open={open}
      onOpenChange={(next) => {
        if (!switching) {
          setOpen(next)
          if (!next) {
            setSearch("")
          }
        }
      }}
    >
      <PopoverTrigger asChild>
        <MenuTrigger
          disabled={switching}
          aria-label={`Switch organization, current organization is ${organization.name}`}
          leading={
            <Avatar
              size="sm"
              name={organization.name}
              src={organization.imageUrl}
              decorative
            />
          }
        >
          {organization.name}
        </MenuTrigger>
      </PopoverTrigger>
      <PopoverContent
        aria-label="Switch organization"
        side="top"
        collisionPadding={8}
        className="flex max-h-[var(--radix-popover-content-available-height)] w-72 flex-col gap-2 overflow-hidden p-2"
      >
        <SearchInput
          className="shrink-0"
          aria-label="Search organizations on this page"
          placeholder="Search organizations…"
          value={search}
          disabled={switching}
          onChange={(event) => setSearch(event.target.value)}
          onClear={() => setSearch("")}
        />
        <div
          className="min-h-0 max-h-64 overflow-y-auto overscroll-contain"
          aria-label="Organizations"
        >
          {userMemberships?.error && (
            <div className="space-y-2 p-2">
              <p role="alert" className="text-sm text-coval-danger-text">
                Could not refresh organizations.
              </p>
              <Button
                variant="secondary"
                disabled={switching || userMemberships.isFetching}
                onClick={() => void userMemberships.revalidate()}
              >
                Retry
              </Button>
            </div>
          )}
          {!isLoaded ||
          (userMemberships?.isLoading &&
            userMemberships.count === undefined) ? (
            <p className="p-2 text-sm text-coval-text-muted">
              Loading organizations…
            </p>
          ) : (
            visibleOrganizations.map((item) => (
              <PopoverItem
                key={item.id}
                checked={item.id === organization.id}
                disabled={switching}
                onClick={() => void switchOrganization(item)}
              >
                <Avatar
                  size="sm"
                  name={item.name}
                  src={item.imageUrl}
                  decorative
                />
                <span
                  className={
                    item.id === organization.id
                      ? "truncate font-bold"
                      : "truncate"
                  }
                >
                  {item.name}
                </span>
              </PopoverItem>
            ))
          )}
          {isLoaded &&
            !userMemberships?.isLoading &&
            !userMemberships?.error &&
            !filtered.length && (
              <p className="p-2 text-sm text-coval-text-muted">
                No other matching organizations on this page.
              </p>
            )}
        </div>
        {(userMemberships?.hasNextPage || userMemberships?.hasPreviousPage) && (
          <div className="flex shrink-0 items-center justify-between gap-2">
            <Button
              variant="ghost"
              disabled={
                switching ||
                userMemberships.isFetching ||
                !userMemberships.hasPreviousPage
              }
              onClick={() => {
                setSearch("")
                userMemberships.fetchPrevious()
              }}
            >
              Previous
            </Button>
            <span className="text-xs text-coval-text-muted">
              Page {userMemberships.page}
            </span>
            <Button
              variant="ghost"
              disabled={
                switching ||
                userMemberships.isFetching ||
                !userMemberships.hasNextPage
              }
              onClick={() => {
                setSearch("")
                userMemberships.fetchNext()
              }}
            >
              Next
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
